import React, { useState, useEffect, useCallback } from 'react';
import { projectsAPI, jobCostingAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, ChevronRight, ChevronDown, Trash2, GripVertical, Loader2, Pencil, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../pages/ProjectsList';
import { ChangeOrders } from './ChangeOrders';
import { InvoicesPayments } from './InvoicesPayments';

// Map cost type names to badge color classes for visual consistency
const COST_TYPE_COLORS = {
  labor: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  materials: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  equipment: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  subcontractor: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  other: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
};

// Fallback for legacy item_type badges
const LEGACY_TYPE_LABELS = {
  labor: 'Labor', material: 'Material', equipment: 'Equipment', subcontract: 'Subcontract', other: 'Other',
};
const LEGACY_TYPE_COLORS = {
  labor: COST_TYPE_COLORS.labor,
  material: COST_TYPE_COLORS.materials,
  equipment: COST_TYPE_COLORS.equipment,
  subcontract: COST_TYPE_COLORS.subcontractor,
  other: COST_TYPE_COLORS.other,
};

// Map cost type name → item_type for backward compat
const NAME_TO_ITEM_TYPE = {
  labor: 'labor', materials: 'material', equipment: 'equipment',
  subcontractor: 'subcontract', other: 'other', permits: 'other',
};

function getBadgeColor(costTypeName) {
  if (!costTypeName) return COST_TYPE_COLORS.other;
  const key = costTypeName.toLowerCase();
  return COST_TYPE_COLORS[key] || COST_TYPE_COLORS.other;
}

export function ProjectBudget({ projectId, canEdit }) {
  const [budget, setBudget] = useState({ groups: [] });
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [addingGroup, setAddingGroup] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [addingItemToGroup, setAddingItemToGroup] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editGroupName, setEditGroupName] = useState('');
  const [itemForm, setItemForm] = useState({
    name: '', cost_type_id: '', cost_code_id: '', costing_unit_id: '',
    quantity: 1, unit_cost: 0, sell_price: '', markup_percent: '',
    item_type: 'other',
  });

  // Job Costing settings
  const [costTypes, setCostTypes] = useState([]);
  const [costCodes, setCostCodes] = useState([]);
  const [costingUnits, setCostingUnits] = useState([]);

  const fetchBudget = useCallback(async () => {
    try {
      const res = await projectsAPI.getBudget(projectId);
      setBudget(res.data);
      const exp = {};
      (res.data.groups || []).forEach(g => { exp[g.id] = true; });
      setExpandedGroups(prev => ({ ...exp, ...prev }));
    } catch {
      toast.error('Failed to load budget');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchBudget(); }, [fetchBudget]);

  // Load costing settings once
  useEffect(() => {
    const load = async () => {
      try {
        const [uRes, ctRes, ccRes] = await Promise.all([
          jobCostingAPI.listUnits(),
          jobCostingAPI.listCostTypes(),
          jobCostingAPI.listCostCodes(),
        ]);
        setCostingUnits(uRes.data.units || []);
        setCostTypes(ctRes.data.cost_types || []);
        setCostCodes(ccRes.data.cost_codes || []);
      } catch { /* silent — dropdowns will just be empty */ }
    };
    load();
  }, []);

  const toggleGroup = (gid) => setExpandedGroups(prev => ({ ...prev, [gid]: !prev[gid] }));

  const handleAddGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      await projectsAPI.createBudgetGroup(projectId, { name: newGroupName.trim() });
      setNewGroupName('');
      setAddingGroup(false);
      fetchBudget();
      toast.success('Group added');
    } catch { toast.error('Failed to add group'); }
  };

  const handleRenameGroup = async (gid) => {
    if (!editGroupName.trim()) return;
    try {
      await projectsAPI.updateBudgetGroup(projectId, gid, { name: editGroupName.trim() });
      setEditingGroupId(null);
      fetchBudget();
    } catch { toast.error('Failed to rename group'); }
  };

  const handleDeleteGroup = async (gid, gname) => {
    if (!window.confirm(`Delete "${gname}" and all its items?`)) return;
    try {
      await projectsAPI.deleteBudgetGroup(projectId, gid);
      fetchBudget();
      toast.success('Group deleted');
    } catch { toast.error('Failed to delete group'); }
  };

  const handleAddItem = async (gid) => {
    if (!itemForm.name.trim()) { toast.error('Item name is required'); return; }

    // Resolve unit abbreviation from costing_unit_id for the `unit` string field
    const unitObj = costingUnits.find(u => u.id === itemForm.costing_unit_id);
    const unitStr = unitObj ? (unitObj.abbreviation || unitObj.name) : (itemForm.unit || undefined);

    try {
      await projectsAPI.createBudgetItem(projectId, {
        group_id: gid,
        name: itemForm.name.trim(),
        item_type: itemForm.item_type || 'other',
        quantity: parseFloat(itemForm.quantity) || 1,
        unit: unitStr,
        unit_cost: parseFloat(itemForm.unit_cost) || 0,
        sell_price: itemForm.sell_price ? parseFloat(itemForm.sell_price) : undefined,
        markup_percent: itemForm.markup_percent !== '' ? parseFloat(itemForm.markup_percent) : undefined,
        cost_type_id: itemForm.cost_type_id || undefined,
        cost_code_id: itemForm.cost_code_id || undefined,
        costing_unit_id: itemForm.costing_unit_id || undefined,
      });
      setAddingItemToGroup(null);
      resetItemForm();
      fetchBudget();
      toast.success('Item added');
    } catch { toast.error('Failed to add item'); }
  };

  const handleUpdateItem = async (itemId) => {
    const unitObj = costingUnits.find(u => u.id === itemForm.costing_unit_id);
    const unitStr = unitObj ? (unitObj.abbreviation || unitObj.name) : (itemForm.unit || undefined);

    try {
      await projectsAPI.updateBudgetItem(projectId, itemId, {
        name: itemForm.name.trim(),
        item_type: itemForm.item_type || 'other',
        quantity: parseFloat(itemForm.quantity) || 1,
        unit: unitStr,
        unit_cost: parseFloat(itemForm.unit_cost) || 0,
        sell_price: itemForm.sell_price ? parseFloat(itemForm.sell_price) : undefined,
        markup_percent: itemForm.markup_percent !== '' ? parseFloat(itemForm.markup_percent) : undefined,
        cost_type_id: itemForm.cost_type_id || '',
        cost_code_id: itemForm.cost_code_id || '',
        costing_unit_id: itemForm.costing_unit_id || '',
      });
      setEditingItem(null);
      fetchBudget();
      toast.success('Item updated');
    } catch { toast.error('Failed to update item'); }
  };

  const handleDeleteItem = async (itemId) => {
    try {
      await projectsAPI.deleteBudgetItem(projectId, itemId);
      fetchBudget();
      toast.success('Item removed');
    } catch { toast.error('Failed to delete item'); }
  };

  const onCostTypeChange = (ctId) => {
    const ct = costTypes.find(c => c.id === ctId);
    const updates = { cost_type_id: ctId };
    if (ct) {
      // Auto-populate item_type from name
      updates.item_type = NAME_TO_ITEM_TYPE[ct.name.toLowerCase()] || 'other';
      // Auto-populate markup if not manually set or empty
      if (ct.default_markup != null) {
        updates.markup_percent = ct.default_markup;
      }
    }
    setItemForm(f => ({ ...f, ...updates }));
  };

  const startEditItem = (item) => {
    setEditingItem(item.id);
    setItemForm({
      name: item.name,
      cost_type_id: item.cost_type_id || '',
      cost_code_id: item.cost_code_id || '',
      costing_unit_id: item.costing_unit_id || '',
      item_type: item.item_type || 'other',
      quantity: item.quantity,
      unit: item.unit || '',
      unit_cost: item.unit_cost,
      sell_price: item.sell_price || '',
      markup_percent: item.markup_percent != null ? item.markup_percent : '',
    });
  };

  const resetItemForm = () => setItemForm({
    name: '', cost_type_id: '', cost_code_id: '', costing_unit_id: '',
    quantity: 1, unit_cost: 0, sell_price: '', markup_percent: '',
    item_type: 'other',
  });

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const allItems = budget.groups?.flatMap(g => g.items || []) || [];
  const totalCost = allItems.reduce((acc, i) => acc + (i.total_cost || 0), 0);
  const totalRevenue = allItems.reduce((acc, i) => acc + ((i.sell_price || 0) * (i.quantity || 1)), 0);
  const margin = totalRevenue > 0 ? ((totalRevenue - totalCost) / totalRevenue * 100).toFixed(1) : null;

  return (
    <div className="space-y-4" data-testid="project-budget">
      {/* Grand Total Bar */}
      <div className="bg-muted/30 border rounded-lg p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-6 flex-wrap">
            <TotalItem label="Total Cost" value={formatCurrency(totalCost)} bold />
            <TotalItem label="Total Revenue" value={formatCurrency(totalRevenue)} />
            {margin && <TotalItem label="Margin" value={`${margin}%`} accent={parseFloat(margin) >= 0} />}
            <TotalItem label="Groups" value={budget.groups?.length || 0} />
            <TotalItem label="Items" value={allItems.length} />
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setAddingGroup(true)} data-testid="add-budget-group-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Group
            </Button>
          )}
        </div>
      </div>

      {/* Change Orders Section */}
      <ChangeOrders projectId={projectId} canEdit={canEdit} />

      {/* Invoices & Payments Section */}
      <InvoicesPayments projectId={projectId} canEdit={canEdit} />

      {/* Add Group */}
      {addingGroup && (
        <div className="flex gap-2 items-center border rounded-lg p-3 bg-muted/20" data-testid="new-group-form">
          <Input autoFocus value={newGroupName} onChange={e => setNewGroupName(e.target.value)} placeholder="Group name (e.g., Electrical Rough-In)" className="flex-1" onKeyDown={e => { if (e.key === 'Enter') handleAddGroup(); if (e.key === 'Escape') { setAddingGroup(false); setNewGroupName(''); }}} data-testid="new-group-name-input" />
          <Button size="sm" onClick={handleAddGroup} disabled={!newGroupName.trim()}>Add</Button>
          <Button size="sm" variant="ghost" onClick={() => { setAddingGroup(false); setNewGroupName(''); }}>Cancel</Button>
        </div>
      )}

      {/* Empty state */}
      {budget.groups?.length === 0 && !addingGroup && (
        <div className="text-center py-12 text-muted-foreground" data-testid="budget-empty">
          <p className="text-lg font-medium">No budget groups yet</p>
          <p className="text-sm mt-1">Add a group to start building your project budget.</p>
          {canEdit && (
            <Button className="mt-4" variant="outline" onClick={() => setAddingGroup(true)}>
              <Plus className="w-4 h-4 mr-2" /> Add First Group
            </Button>
          )}
        </div>
      )}

      {/* Groups */}
      {budget.groups?.map(group => {
        const groupItemCount = group.items?.length || 0;
        const groupRevenue = (group.items || []).reduce((a, i) => a + ((i.sell_price || 0) * (i.quantity || 1)), 0);
        const groupMargin = groupRevenue > 0 ? ((groupRevenue - (group.total_cost || 0)) / groupRevenue * 100).toFixed(1) : null;

        return (
          <div key={group.id} className="border rounded-lg overflow-hidden" data-testid={`budget-group-${group.id}`}>
            {/* Group Header */}
            <div className="flex items-center gap-2 p-3 bg-muted/20 cursor-pointer select-none" onClick={() => toggleGroup(group.id)}>
              {expandedGroups[group.id] ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}

              {/* Editable group name */}
              {editingGroupId === group.id ? (
                <div className="flex items-center gap-1 flex-1" onClick={e => e.stopPropagation()}>
                  <Input value={editGroupName} onChange={e => setEditGroupName(e.target.value)} className="h-7 text-sm font-semibold" autoFocus onKeyDown={e => { if (e.key === 'Enter') handleRenameGroup(group.id); if (e.key === 'Escape') setEditingGroupId(null); }} />
                  <button onClick={() => handleRenameGroup(group.id)} className="text-green-600 hover:text-green-700"><Check className="w-4 h-4" /></button>
                  <button onClick={() => setEditingGroupId(null)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <span className="font-semibold text-sm flex-1" onDoubleClick={(e) => { if (canEdit) { e.stopPropagation(); setEditingGroupId(group.id); setEditGroupName(group.name); }}}>{group.name}</span>
              )}

              {/* Group subtotals */}
              <div className="flex items-center gap-4 text-xs mr-2">
                <span className="text-muted-foreground">{groupItemCount} item{groupItemCount !== 1 ? 's' : ''}</span>
                <span className="font-semibold tabular-nums">{formatCurrency(group.total_cost)}</span>
                {groupMargin && <span className={`tabular-nums ${parseFloat(groupMargin) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{groupMargin}%</span>}
              </div>

              {canEdit && (
                <div className="flex gap-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => { setEditingGroupId(group.id); setEditGroupName(group.name); }} className="text-muted-foreground hover:text-foreground p-0.5"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => handleDeleteGroup(group.id, group.name)} className="text-muted-foreground hover:text-red-500 p-0.5" data-testid={`delete-group-${group.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              )}
            </div>

            {/* Items */}
            {expandedGroups[group.id] && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid={`budget-items-table-${group.id}`}>
                  <thead className="bg-muted/10">
                    <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                      <th className="text-left p-2 pl-4 w-8"></th>
                      <th className="text-left p-2 min-w-[140px]">Name</th>
                      <th className="text-left p-2 w-[100px]">Cost Type</th>
                      <th className="text-left p-2 w-[120px]">Cost Code</th>
                      <th className="text-right p-2 w-[70px]">Qty</th>
                      <th className="text-left p-2 w-[80px]">Unit</th>
                      <th className="text-right p-2 w-[90px]">Unit Cost</th>
                      <th className="text-right p-2 w-[100px] font-semibold">Ext. Cost</th>
                      <th className="text-right p-2 w-[80px]">Markup</th>
                      <th className="text-right p-2 w-[90px]">Sell/Unit</th>
                      {canEdit && <th className="w-[60px]"></th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(group.items || []).map(item => (
                      editingItem === item.id ? (
                        <ItemEditRow key={item.id} form={itemForm} setForm={setItemForm}
                          onCostTypeChange={onCostTypeChange}
                          costTypes={costTypes} costCodes={costCodes} costingUnits={costingUnits}
                          onSave={() => handleUpdateItem(item.id)} onCancel={() => setEditingItem(null)} canEdit={canEdit} />
                      ) : (
                        <tr key={item.id} className="hover:bg-muted/10 group/row" data-testid={`budget-item-${item.id}`}>
                          <td className="p-2 pl-4"><GripVertical className="w-3.5 h-3.5 text-muted-foreground/30 group-hover/row:text-muted-foreground/60" /></td>
                          <td className="p-2">
                            <span className="font-medium">{item.name}</span>
                            {item.description && <span className="text-xs text-muted-foreground block mt-0.5 truncate max-w-[200px]">{item.description}</span>}
                          </td>
                          <td className="p-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              item.cost_type_name
                                ? getBadgeColor(item.cost_type_name)
                                : (LEGACY_TYPE_COLORS[item.item_type] || LEGACY_TYPE_COLORS.other)
                            }`}>
                              {item.cost_type_name || LEGACY_TYPE_LABELS[item.item_type] || item.item_type}
                            </span>
                          </td>
                          <td className="p-2 text-xs text-muted-foreground">{item.cost_code_label || '—'}</td>
                          <td className="p-2 text-right tabular-nums">{item.quantity}</td>
                          <td className="p-2 text-muted-foreground text-xs">{item.costing_unit_name || item.unit || '—'}</td>
                          <td className="p-2 text-right tabular-nums">{formatCurrency(item.unit_cost)}</td>
                          <td className="p-2 text-right tabular-nums font-semibold">{formatCurrency(item.total_cost)}</td>
                          <td className="p-2 text-right tabular-nums text-muted-foreground">{item.markup_percent != null ? `${item.markup_percent}%` : '—'}</td>
                          <td className="p-2 text-right tabular-nums">{item.sell_price ? formatCurrency(item.sell_price) : '—'}</td>
                          {canEdit && (
                            <td className="p-2 text-right">
                              <div className="flex gap-1 justify-end opacity-0 group-hover/row:opacity-100 transition-opacity">
                                <button onClick={() => startEditItem(item)} className="text-muted-foreground hover:text-foreground" data-testid={`edit-item-${item.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                                <button onClick={() => handleDeleteItem(item.id)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-item-${item.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                              </div>
                            </td>
                          )}
                        </tr>
                      )
                    ))}
                    {/* Add item row */}
                    {addingItemToGroup === group.id ? (
                      <ItemEditRow form={itemForm} setForm={setItemForm}
                        onCostTypeChange={onCostTypeChange}
                        costTypes={costTypes} costCodes={costCodes} costingUnits={costingUnits}
                        onSave={() => handleAddItem(group.id)} onCancel={() => { setAddingItemToGroup(null); resetItemForm(); }} isNew canEdit={canEdit} />
                    ) : canEdit ? (
                      <tr>
                        <td colSpan={canEdit ? 11 : 10} className="p-2 pl-4">
                          <button
                            onClick={() => { setAddingItemToGroup(group.id); resetItemForm(); }}
                            className="text-sm text-primary/70 hover:text-primary flex items-center gap-1 font-medium"
                            data-testid={`add-item-to-${group.id}`}
                          >
                            <Plus className="w-3.5 h-3.5" /> Add line item
                          </button>
                        </td>
                      </tr>
                    ) : null}
                    {/* Group subtotal row */}
                    {groupItemCount > 0 && (
                      <tr className="bg-muted/10 font-medium text-xs">
                        <td colSpan={7} className="p-2 pl-4 text-right text-muted-foreground uppercase tracking-wide">Subtotal</td>
                        <td className="p-2 text-right tabular-nums font-bold">{formatCurrency(group.total_cost)}</td>
                        <td></td>
                        <td className="p-2 text-right tabular-nums">{groupRevenue > 0 ? formatCurrency(groupRevenue) : ''}</td>
                        {canEdit && <td></td>}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function TotalItem({ label, value, bold, accent }) {
  return (
    <div className="text-sm">
      <span className="text-muted-foreground">{label}: </span>
      <span className={`tabular-nums ${bold ? 'font-bold text-lg' : 'font-semibold'} ${accent !== undefined ? (accent ? 'text-green-600' : 'text-red-600') : ''}`}>{value}</span>
    </div>
  );
}

function ItemEditRow({ form, setForm, onSave, onCancel, isNew, canEdit, costTypes, costCodes, costingUnits, onCostTypeChange }) {
  const liveTotal = (parseFloat(form.quantity) || 0) * (parseFloat(form.unit_cost) || 0);

  // Build cost code options: top-level parents, then indented children
  const topLevelCodes = costCodes.filter(c => !c.parent_id);
  const codeOptions = [];
  for (const parent of topLevelCodes) {
    codeOptions.push(parent);
    const children = costCodes.filter(c => c.parent_id === parent.id);
    children.sort((a, b) => a.code.localeCompare(b.code));
    codeOptions.push(...children);
  }

  return (
    <tr className="bg-primary/5" data-testid="budget-item-edit-row">
      <td className="p-2 pl-4"></td>
      {/* Name */}
      <td className="p-1">
        <Input autoFocus value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
          placeholder="Item name" className="h-8 text-sm"
          onKeyDown={e => { if (e.key === 'Enter') onSave(); if (e.key === 'Escape') onCancel(); }}
          data-testid="budget-item-name-input" />
      </td>
      {/* Cost Type */}
      <td className="p-1">
        <Select value={form.cost_type_id || '_none'} onValueChange={v => {
          if (v === '_none') { setForm(f => ({ ...f, cost_type_id: '', item_type: 'other' })); }
          else { onCostTypeChange(v); }
        }}>
          <SelectTrigger className="h-8 text-xs" data-testid="budget-item-cost-type-select"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— None —</SelectItem>
            {costTypes.map(ct => <SelectItem key={ct.id} value={ct.id}>{ct.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      {/* Cost Code */}
      <td className="p-1">
        <Select value={form.cost_code_id || '_none'} onValueChange={v => setForm(f => ({...f, cost_code_id: v === '_none' ? '' : v}))}>
          <SelectTrigger className="h-8 text-xs" data-testid="budget-item-cost-code-select"><SelectValue placeholder="Code" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">— None —</SelectItem>
            {codeOptions.map(cc => (
              <SelectItem key={cc.id} value={cc.id}>
                {cc.parent_id ? `  ${cc.code} ${cc.name}` : `${cc.code} ${cc.name}`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      {/* Qty */}
      <td className="p-1">
        <Input type="number" value={form.quantity} onChange={e => setForm(f => ({...f, quantity: e.target.value}))}
          className="h-8 text-sm text-right w-[60px]" data-testid="budget-item-qty-input" />
      </td>
      {/* Unit */}
      <td className="p-1">
        <Select value={form.costing_unit_id || '_none'} onValueChange={v => setForm(f => ({...f, costing_unit_id: v === '_none' ? '' : v}))}>
          <SelectTrigger className="h-8 text-xs" data-testid="budget-item-unit-select"><SelectValue placeholder="Unit" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="_none">—</SelectItem>
            {costingUnits.map(u => <SelectItem key={u.id} value={u.id}>{u.abbreviation || u.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </td>
      {/* Unit Cost */}
      <td className="p-1">
        <Input type="number" value={form.unit_cost} onChange={e => setForm(f => ({...f, unit_cost: e.target.value}))}
          className="h-8 text-sm text-right w-[80px]" data-testid="budget-item-unit-cost-input" />
      </td>
      {/* Ext. Cost (computed) */}
      <td className="p-1 text-right text-sm font-semibold tabular-nums">{formatCurrency(liveTotal)}</td>
      {/* Markup */}
      <td className="p-1">
        <Input type="number" value={form.markup_percent} onChange={e => setForm(f => ({...f, markup_percent: e.target.value}))}
          placeholder="%" className="h-8 text-sm text-right w-[60px]" data-testid="budget-item-markup-input" />
      </td>
      {/* Sell/Unit */}
      <td className="p-1">
        <Input type="number" value={form.sell_price} onChange={e => setForm(f => ({...f, sell_price: e.target.value}))}
          placeholder="—" className="h-8 text-sm text-right w-[80px]" data-testid="budget-item-sell-input" />
      </td>
      {/* Actions */}
      {canEdit && (
        <td className="p-1">
          <div className="flex gap-1 justify-end">
            <Button size="sm" className="h-7 text-xs px-2" onClick={onSave} data-testid="budget-item-save-btn">{isNew ? 'Add' : 'Save'}</Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs px-2" onClick={onCancel}>
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </td>
      )}
    </tr>
  );
}
